import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // Use the public close method to ensure all submenus are closed
    this.close();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // On desktop, close any other open submenus before opening this one
    if (window.matchMedia('(min-width: 750px)').matches && details !== this.refs.details) {
      const allSubmenus = this.querySelectorAll('.menu-drawer__menu-container[open]');
      allSubmenus.forEach(submenu => {
        if (submenu instanceof HTMLDetailsElement && submenu !== details) {
          this.#close(submenu);
        }
      });
    }

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    const details = this.#getDetailsElement(event);
    
    // On desktop (width >= 750px), only close the submenu, not the main drawer
    if (window.matchMedia('(min-width: 750px)').matches) {
      // Find the closest submenu details element
      const submenuDetails = details.closest('.menu-drawer__menu-container');
      if (submenuDetails && submenuDetails instanceof HTMLDetailsElement && submenuDetails !== this.refs.details) {
        this.#close(submenuDetails);
        return;
      }
    }
    
    this.#close(details);
  }

  /**
   * Close the main menu drawer
   */
  close() {
    // Close all open submenus first
    const allOpenSubmenus = this.querySelectorAll('.menu-drawer__menu-container[open]');
    allOpenSubmenus.forEach(submenu => {
      if (submenu instanceof HTMLDetailsElement) {
        submenu.classList.remove('menu-open');
        reset(submenu);
      }
    });
    
    // Then close the main drawer
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Use a more reliable method to wait for animation completion
    const submenu = details.querySelector('.menu-drawer__submenu');
    if (submenu) {
      let hasHandled = false;
      
      const handleTransitionEnd = () => {
        if (hasHandled) return;
        hasHandled = true;
        
        submenu.removeEventListener('transitionend', handleTransitionEnd);
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      };
      
      submenu.addEventListener('transitionend', handleTransitionEnd, { once: true });
      
      // Fallback in case transitionend doesn't fire
      setTimeout(() => {
        if (!hasHandled) {
          handleTransitionEnd();
        }
      }, 500);
    } else {
      // For elements without submenu, use the original method
      onAnimationEnd(details, () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      });
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  
  // Reset any animation state by removing and re-adding the element's class
  // This ensures animations will work on the next open
  const submenu = element.querySelector('.menu-drawer__submenu');
  if (submenu instanceof HTMLElement) {
    submenu.style.animation = 'none';
    // Force reflow to ensure the animation reset takes effect
    submenu.offsetHeight;
    submenu.style.animation = '';
  }
}
